import { useQueries, UseQueryResult } from '@tanstack/react-query';

import { edgeStackQueryOptions } from '@/react/edge/edge-stacks/queries/useEdgeStack';
import { stackQueryOptions } from '@/react/common/stacks/queries/useStack';
import { EdgeStack } from '@/react/edge/edge-stacks/types';
import { Stack } from '@/react/common/stacks/types';

import { workflowQueryKeys } from '../../queries/query-keys';
import { WorkflowArtifact, WorkflowType } from '../types';

export type MixedArtifactObject = EdgeStack | Stack;
export type MaybeArtifactObject = MixedArtifactObject | null | undefined;

export function isStack(maybe: MaybeArtifactObject): maybe is Stack {
  return !!maybe && 'Type' in maybe;
}

export function isEdgeStack(maybe: MaybeArtifactObject): maybe is EdgeStack {
  return !!maybe && !isStack(maybe);
}

export function isHelmObject(obj: MixedArtifactObject) {
  return (
    (isEdgeStack(obj) && !!obj.HelmConfig) ||
    (isStack(obj) && !!obj.HelmChartPath)
  );
}

export function getRealObject(
  data: MixedArtifactObject[],
  id: number,
  type: WorkflowType
) {
  return data.find(
    (i) =>
      id === i.Id &&
      ((type === 'edgeStack' && isEdgeStack(i)) ||
        (type === 'stack' && isStack(i)))
  );
}

export function useArtifactUnderlyingObjects(
  artifacts: Array<WorkflowArtifact>
): UseQueryResult<MaybeArtifactObject>[] {
  return useQueries({
    queries: artifacts.map((artifact) => artifactQueryOptions(artifact)),
  });
}

/**
 * Chooses what to fetch for an artifact, or nothing at all.
 *
 * A reconciled deployment has no underlying stack object: the engine applies to
 * environments directly and keeps its own record. Falling through to the stack
 * query asks for an identifier that belongs to a different bucket, which answers
 * 404 for every artifact of that kind.
 */
function artifactQueryOptions(artifact: WorkflowArtifact) {
  switch (artifact.type) {
    case 'edgeStack':
      return edgeStackQueryOptions(artifact.id);
    case 'gitopsDeployment':
      return {
        queryKey: [
          ...workflowQueryKeys.deployment(artifact.id),
          'no-underlying-object',
        ],
        queryFn: () => null,
        enabled: false,
      };
    case 'stack':
    default:
      return stackQueryOptions(artifact.id);
  }
}
